import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sun, Moon, Monitor, Clock } from 'lucide-react';
import { toZonedTime, format } from 'date-fns-tz';

const COMMON_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (UTC-5)', region: 'Americas' },
  { value: 'America/Chicago', label: 'Central Time (UTC-6)', region: 'Americas' },
  { value: 'America/Denver', label: 'Mountain Time (UTC-7)', region: 'Americas' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (UTC-8)', region: 'Americas' },
  { value: 'America/Anchorage', label: 'Alaska Time (UTC-9)', region: 'Americas' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (UTC-10)', region: 'Americas' },
  { value: 'America/Toronto', label: 'Toronto (UTC-5)', region: 'Americas' },
  { value: 'America/Vancouver', label: 'Vancouver (UTC-8)', region: 'Americas' },
  { value: 'America/Mexico_City', label: 'Mexico City (UTC-6)', region: 'Americas' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (UTC-3)', region: 'Americas' },
  { value: 'Europe/London', label: 'London (UTC+0)', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris (UTC+1)', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin (UTC+1)', region: 'Europe' },
  { value: 'Europe/Rome', label: 'Rome (UTC+1)', region: 'Europe' },
  { value: 'Europe/Madrid', label: 'Madrid (UTC+1)', region: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (UTC+1)', region: 'Europe' },
  { value: 'Europe/Brussels', label: 'Brussels (UTC+1)', region: 'Europe' },
  { value: 'Europe/Vienna', label: 'Vienna (UTC+1)', region: 'Europe' },
  { value: 'Europe/Stockholm', label: 'Stockholm (UTC+1)', region: 'Europe' },
  { value: 'Europe/Warsaw', label: 'Warsaw (UTC+1)', region: 'Europe' },
  { value: 'Europe/Athens', label: 'Athens (UTC+2)', region: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow (UTC+3)', region: 'Europe' },
  { value: 'Asia/Dubai', label: 'Dubai (UTC+4)', region: 'Asia' },
  { value: 'Asia/Kolkata', label: 'India (UTC+5:30)', region: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore (UTC+8)', region: 'Asia' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (UTC+8)', region: 'Asia' },
  { value: 'Asia/Shanghai', label: 'Shanghai (UTC+8)', region: 'Asia' },
  { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)', region: 'Asia' },
  { value: 'Asia/Seoul', label: 'Seoul (UTC+9)', region: 'Asia' },
  { value: 'Australia/Sydney', label: 'Sydney (UTC+11)', region: 'Pacific' },
  { value: 'Australia/Melbourne', label: 'Melbourne (UTC+11)', region: 'Pacific' },
  { value: 'Pacific/Auckland', label: 'Auckland (UTC+13)', region: 'Pacific' },
];

const groupedTimezones = COMMON_TIMEZONES.reduce((acc, tz) => {
  if (!acc[tz.region]) acc[tz.region] = [];
  acc[tz.region].push(tz);
  return acc;
}, {} as Record<string, typeof COMMON_TIMEZONES>);

export function ThemeSettings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [detectedTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);

  const { data: preferences } = useQuery({
    queryKey: ['theme-preferences', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('theme_mode, timezone, dark_mode_start, dark_mode_end')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const updatePreferences = useMutation({
    mutationFn: async (updates: {
      theme_mode?: string;
      timezone?: string;
      dark_mode_start?: string;
      dark_mode_end?: string;
    }) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['theme-preferences', user?.id] });
      toast.success('Theme preferences updated');
    },
    onError: (error: any) => {
      toast.error('Failed to update preferences', {
        description: error.message,
      });
    },
  });

  const themeMode = preferences?.theme_mode || 'system';
  const timezone = preferences?.timezone || detectedTimezone;
  const darkModeStart = preferences?.dark_mode_start || '20:00:00';
  const darkModeEnd = preferences?.dark_mode_end || '07:00:00';

  const getCurrentStatus = () => {
    if (themeMode !== 'scheduled') return null;

    const now = new Date();
    const zonedNow = toZonedTime(now, timezone);
    const currentTime = format(zonedNow, 'HH:mm:ss', { timeZone: timezone });

    const isInDarkPeriod = (current: string, start: string, end: string) => {
      if (start < end) {
        return current >= start && current < end;
      } else {
        return current >= start || current < end;
      }
    };

    const isDark = isInDarkPeriod(currentTime, darkModeStart, darkModeEnd);
    return isDark ? 'dark' : 'light';
  };

  const currentStatus = getCurrentStatus();

  return (
    <Card className="p-6 space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-1">Appearance</h3>
        <p className="text-sm text-muted-foreground">
          Customize how the app looks for you
        </p>
      </div>

      <div className="space-y-4">
        <Label>Theme Mode</Label>
        <RadioGroup
          value={themeMode}
          onValueChange={(value) => updatePreferences.mutate({ theme_mode: value })}
          className="grid grid-cols-2 gap-4"
        >
          <div>
            <RadioGroupItem value="light" id="light" className="peer sr-only" />
            <Label
              htmlFor="light"
              className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
            >
              <Sun className="mb-3 h-6 w-6" />
              Light
            </Label>
          </div>
          <div>
            <RadioGroupItem value="dark" id="dark" className="peer sr-only" />
            <Label
              htmlFor="dark"
              className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
            >
              <Moon className="mb-3 h-6 w-6" />
              Dark
            </Label>
          </div>
          <div>
            <RadioGroupItem value="system" id="system" className="peer sr-only" />
            <Label
              htmlFor="system"
              className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
            >
              <Monitor className="mb-3 h-6 w-6" />
              System
            </Label>
          </div>
          <div>
            <RadioGroupItem value="scheduled" id="scheduled" className="peer sr-only" />
            <Label
              htmlFor="scheduled"
              className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
            >
              <Clock className="mb-3 h-6 w-6" />
              Scheduled
            </Label>
          </div>
        </RadioGroup>
      </div>

      {themeMode === 'scheduled' && (
        <div className="space-y-4 pt-4 border-t">
          <div className="space-y-2">
            <Label htmlFor="timezone">Timezone</Label>
            <Select
              value={timezone}
              onValueChange={(value) => updatePreferences.mutate({ timezone: value })}
            >
              <SelectTrigger id="timezone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(groupedTimezones).map(([region, timezones]) => (
                  <div key={region}>
                    <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
                      {region}
                    </div>
                    {timezones.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start-time">Dark Mode Starts</Label>
              <input
                id="start-time"
                type="time"
                value={darkModeStart.slice(0, 5)}
                onChange={(e) => updatePreferences.mutate({ dark_mode_start: e.target.value + ':00' })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-time">Dark Mode Ends</Label>
              <input
                id="end-time"
                type="time"
                value={darkModeEnd.slice(0, 5)}
                onChange={(e) => updatePreferences.mutate({ dark_mode_end: e.target.value + ':00' })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          </div>

          <div className="rounded-lg bg-muted p-4 space-y-2">
            <p className="text-sm font-medium">Schedule Preview</p>
            <p className="text-sm text-muted-foreground">
              Dark mode will be active from {darkModeStart.slice(0, 5)} to {darkModeEnd.slice(0, 5)} in your timezone
            </p>
            {currentStatus && (
              <p className="text-sm text-muted-foreground">
                Currently: <span className="font-medium">{currentStatus === 'dark' ? 'Dark Mode' : 'Light Mode'}</span>
              </p>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
